import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MODULES, getModule, STICKY } from "../data/modules";
import { Sketch } from "../components/Sketches";
import { Mark, Reveal, Example, Hinglish, Points, Steps, Chips, Formulas, NoteTable, Playbooks, Sticky } from "../components/NoteBlocks";
import { Calculator } from "../components/Calculator";
import { Quiz } from "../components/Quiz";

const Section = ({ s, n, color }) => (
  <section id={s.id} className="note-section" data-testid={`section-${s.id}`}>
    <Reveal>
      <h2 className="section-h"><span className="font-mono text-sm text-[#E4572E] mr-3 align-middle">{n}</span>{s.h}</h2>
    </Reveal>
    <Reveal delay={0.05}>
      {s.p?.map((t) => <p key={t} className="text-[16px] sm:text-[17px] leading-[1.85] text-[#2E2E38] mt-4"><Mark text={t} /></p>)}
      {s.sketch && <Sketch name={s.sketch} />}
      {s.formulas && <Formulas items={s.formulas} />}
      {s.table && <NoteTable table={s.table} testId={`table-${s.id}`} />}
      {s.chips && <Chips items={s.chips} />}
      {s.code && <pre className="code-note" data-testid={`code-${s.id}`}>{s.code}</pre>}
      {s.steps && <Steps items={s.steps} />}
      {s.points && <Points items={s.points} />}
      {s.playbooks && <Playbooks items={s.playbooks} />}
      {s.example && <Example ex={s.example} color={color} testId={`example-${s.id}`} />}
      {s.example2 && <Example ex={s.example2} color="blue" rotate={0.8} testId={`example2-${s.id}`} />}
      {s.hinglish && <Hinglish text={s.hinglish} />}
    </Reveal>
  </section>
);

const Toc = ({ m }) => (
  <aside className="hidden lg:block">
    <div className="sticky top-24">
      <p className="eyebrow mb-4">On this page</p>
      <ul className="space-y-2 border-l-2 border-[#1F2A44]/15">
        {m.sections.map((s) => (
          <li key={s.id}><Link to={`#${s.id}`} className="toc-link" data-testid={`toc-link-${s.id}`}>{s.h}</Link></li>
        ))}
        <li><Link to="#quiz" className="toc-link" data-testid="toc-link-quiz">Quick quiz</Link></li>
      </ul>
    </div>
  </aside>
);

const PrevNext = ({ id }) => {
  const prev = MODULES[id - 2], next = MODULES[id];
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-16">
      {prev ? <Link to={`/module/${prev.slug}`} className="nav-card" data-testid="prev-module-link"><ArrowLeft size={18} /> <span><small>Module {prev.id}</small>{prev.title}</span></Link> : <span />}
      {next && <Link to={`/module/${next.slug}`} className="nav-card justify-end text-right" data-testid="next-module-link"><span><small>Module {next.id}</small>{next.title}</span> <ArrowRight size={18} /></Link>}
    </div>
  );
};

export default function ModulePage() {
  const { slug } = useParams();
  const m = getModule(slug);
  if (!m) return <Navigate to="/" replace />;
  return (
    <main className="pt-24" data-testid={`module-page-${m.id}`}>
      <header className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-12 grid md:grid-cols-[auto_1fr] gap-6 md:gap-12 items-end">
        <div className="overflow-hidden">
          <motion.p initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: [0.77, 0, 0.175, 1] }}
            className="font-display text-[34vw] md:text-[15rem] leading-[0.75] text-[#1F2A44]" style={{ WebkitTextStroke: "0" }}>
            {String(m.id).padStart(2, "0")}
          </motion.p>
        </div>
        <div className="pb-2">
          <p className="eyebrow">Module {m.id} of 10</p>
          <div className="overflow-hidden mt-2">
            <motion.h1 initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.77, 0, 0.175, 1] }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1F2A44] leading-[0.95]" data-testid="module-title">{m.title}</motion.h1>
          </div>
          <motion.div initial={{ opacity: 0, rotate: -6, y: 20 }} animate={{ opacity: 1, rotate: -1.5, y: 0 }} transition={{ delay: 0.6, type: "spring", stiffness: 120 }} className="inline-block mt-6">
            <span className="font-hand text-xl px-4 py-2 inline-block shadow-sm" style={{ background: STICKY[m.color] }}>{m.tagline}</span>
          </motion.div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[220px_1fr] gap-10 pb-24">
        <Toc m={m} />
        <div className="notebook-page min-w-0">
          {m.sections.map((s, i) => <Section key={s.id} s={s} n={`${m.id}.${i + 1}`} color={m.color} />)}
          {m.id === 2 && (
            <section className="note-section" data-testid="module-calculator">
              <h2 className="section-h">Try the formulas</h2>
              <div className="mt-6"><Calculator /></div>
            </section>
          )}
          <section className="note-section">
            <h2 className="section-h">Revision notes</h2>
            <Sticky color="yellow" rotate={-0.8} className="mt-6 max-w-2xl" testId="module-takeaways">
              <ul className="space-y-2">{m.takeaways.map((t, i) => <li key={t} className="font-hand text-xl">{i + 1}. {t}</li>)}</ul>
            </Sticky>
          </section>
          <div id="quiz" className="note-section"><Quiz items={m.quiz} moduleId={m.id} /></div>
          <PrevNext id={m.id} />
        </div>
      </div>
    </main>
  );
}
